/**
 * DashboardSettings
 * 
 * Collapsible settings panel for dashboard-wide settings.
 * Appears when clicking the settings icon in the dashboard header.
 * Contains date range picker and projection toggle.
 */

import React, { useRef, useEffect } from 'react';
import { DatePicker, Switch, Button, Tooltip } from 'antd';
import {
  SettingOutlined,
  CalendarOutlined,
  LineChartOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useDashboardSettings } from '../../contexts/DashboardSettingsContext';
import styles from './DashboardSettings.module.css';

const { RangePicker } = DatePicker;

// ============================================================================
// SETTINGS TRIGGER BUTTON
// ============================================================================

interface DashboardSettingsButtonProps {
  className?: string;
}

export const DashboardSettingsButton: React.FC<DashboardSettingsButtonProps> = ({ className }) => {
  const { toggleSettingsPanel, isSettingsPanelOpen } = useDashboardSettings();

  return (
    <Tooltip title="Control Settings">
      <button
        className={`${styles.settingsButton} ${isSettingsPanelOpen ? styles.active : ''} ${className || ''}`}
        onClick={toggleSettingsPanel}
        aria-label="Control Settings"
        aria-expanded={isSettingsPanelOpen}
      >
        <SettingOutlined />
      </button>
    </Tooltip>
  );
};

// ============================================================================
// SETTINGS PANEL
// ============================================================================

interface DashboardSettingsPanelProps {
  className?: string;
}

export const DashboardSettingsPanel: React.FC<DashboardSettingsPanelProps> = ({ className }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    dateRange,
    showProjections,
    isSettingsPanelOpen,
    setDateRange,
    setShowProjections,
    closeSettingsPanel,
  } = useDashboardSettings();

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        // Check if click was on the settings button (to avoid double-toggle)
        const target = event.target as HTMLElement;
        if (!target.closest(`.${styles.settingsButton}`)) {
          closeSettingsPanel();
        }
      }
    };

    if (isSettingsPanelOpen) {
      // Delay to avoid immediate close on the same click
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSettingsPanelOpen, closeSettingsPanel]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSettingsPanelOpen) {
        closeSettingsPanel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSettingsPanelOpen, closeSettingsPanel]);

  if (!isSettingsPanelOpen) {
    return null;
  }

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange({
      startDate: dates?.[0]?.toDate() || null,
      endDate: dates?.[1]?.toDate() || null,
    });
  };

  const handleClearDates = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  return (
    <div ref={panelRef} className={`${styles.settingsPanel} ${className || ''}`}>
      <div className={styles.panelHeader}>
        <h4 className={styles.panelTitle}>
          <SettingOutlined />
          Dashboard Settings
        </h4>
        <button
          className={styles.closeButton}
          onClick={closeSettingsPanel}
          aria-label="Close settings"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className={styles.panelContent}>
        {/* Date Range */}
        <div className={styles.settingGroup}>
          <label className={styles.settingLabel}>
            <CalendarOutlined />
            Date Range
          </label>
          <div className={styles.dateRangeRow}>
            <RangePicker
              value={[
                dateRange.startDate ? dayjs(dateRange.startDate) : null,
                dateRange.endDate ? dayjs(dateRange.endDate) : null,
              ]}
              onChange={handleDateRangeChange}
              size="middle"
              className={styles.datePicker}
              placeholder={['Start Date', 'End Date']}
            />
            {(dateRange.startDate || dateRange.endDate) && (
              <Button
                size="small"
                type="text"
                onClick={handleClearDates}
                className={styles.clearButton}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Projections Toggle */}
        <div className={styles.settingGroup}>
          <label className={styles.settingLabel}>
            <LineChartOutlined />
            Show Projections
          </label>
          <Switch
            checked={showProjections}
            onChange={setShowProjections}
            className={styles.projectionSwitch}
          />
        </div>
      </div>

      <div className={styles.panelFooter}>
        <Button
          type="primary"
          size="small"
          onClick={closeSettingsPanel}
          className={styles.applyButton}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// COMBINED COMPONENT
// ============================================================================

interface DashboardSettingsProps {
  className?: string;
}

export const DashboardSettings: React.FC<DashboardSettingsProps> = ({ className }) => {
  return (
    <div className={`${styles.settingsContainer} ${className || ''}`}>
      <DashboardSettingsButton />
      <DashboardSettingsPanel />
    </div>
  );
};

export default DashboardSettings;
